import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import Modal from '../components/Modal';
import Field from '../components/Field';
import Btn from '../components/Btn';
import MultiFileUpload from '../components/MultiFileUpload';

export default function NewRequestModal({ onClose }) {
  const { currentUser, beneficiaries, projects, paymentTypes, addRequest, addBeneficiary } = useApp();
  const [mode, setMode] = useState('existing');
  const [selectedBen, setSelectedBen] = useState('');
  const [form, setForm] = useState({
    amount: '', currency: 'EGP', description: '', projectId: '', paymentTypeId: '',
    attachedInvoice: '', attachedDoc: '', jobDateFrom: '', jobDateTo: '',
    paymentDirect: 'yes', recipientType: 'self', recipientBenId: '', invoiceAvailable: 'yes',
  });
  const [newBen, setNewBen] = useState({ name: '', type: 'individual', address: '', phone: '', email: '', bankName: '', bankAddress: '', accountNumber: '', iban: '', swiftCode: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const myProjects = projects.filter(p => p.status === 'active' && (p.assignedPM === currentUser?.id || currentUser?.role !== 'pm'));
  const ben = beneficiaries.find(b => b.id === selectedBen);

  const submit = async (e) => {
    e.preventDefault(); setError('');
    if (!form.jobDateFrom || !form.jobDateTo) { setError('Please enter the job dates.'); return; }
    if (!form.projectId) { setError('Please select a project.'); return; }
    if (!form.paymentTypeId) { setError('Please select a payment type.'); return; }

    setSubmitting(true);
    let resolvedBen = ben;
    if (mode === 'new') {
      resolvedBen = await addBeneficiary({ ...newBen, assignedPM: currentUser.id, createdBy: currentUser.id, lastModifiedBy: currentUser.id });
    }
    if (!resolvedBen) { setError('Please select a beneficiary.'); setSubmitting(false); return; }

    const proj = projects.find(p => p.id === form.projectId);
    const pt = paymentTypes.find(p => p.id === form.paymentTypeId);

    // Resolve reimbursement recipient
    let recipient = { name: '', bank: '', bankAddress: '', account: '', iban: '', swift: '' };
    const isDirect = form.paymentDirect === 'yes';
    if (!isDirect) {
      if (form.recipientType === 'self') {
        recipient = { name: currentUser.name + ' (PM — reimbursement)', bank: '', bankAddress: '', account: '', iban: '', swift: '' };
      } else if (form.recipientType === 'beneficiary') {
        const rb = beneficiaries.find(b => b.id === form.recipientBenId);
        if (!rb) { setError('Please select the reimbursement recipient.'); setSubmitting(false); return; }
        recipient = { name: rb.name, bank: rb.bankName, bankAddress: rb.bankAddress, account: rb.accountNumber, iban: rb.iban, swift: rb.swiftCode };
      }
    }

    await addRequest({
      createdBy: currentUser.id, createdByRole: 'pm',
      beneficiaryId: resolvedBen.id, beneficiaryName: resolvedBen.name,
      projectId: form.projectId, projectName: proj ? proj.name : '',
      pmId: currentUser.id, pmName: currentUser.name,
      paymentTypeId: form.paymentTypeId, paymentType: pt ? pt.name : '',
      amount: parseFloat(form.amount), currency: form.currency, description: form.description,
      attachedInvoice: form.attachedInvoice, attachedDoc: form.attachedDoc,
      jobDateFrom: form.jobDateFrom, jobDateTo: form.jobDateTo,
      paymentDirect: isDirect,
      invoiceAvailable: form.invoiceAvailable === 'yes',
      recipientName: recipient.name, recipientBank: recipient.bank, recipientBankAddress: recipient.bankAddress,
      recipientAccount: recipient.account, recipientIban: recipient.iban, recipientSwift: recipient.swift,
      status: 'Submitted to Accountant',
    });
    setDone(true); setSubmitting(false);
  };

  if (done) return (
    <Modal title="Request submitted" onClose={onClose}>
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
        <p style={{ color: '#444', marginBottom: 20 }}>Payment request submitted to the accountant for review.</p>
        <Btn onClick={onClose}>Close</Btn>
      </div>
    </Modal>
  );

  return (
    <Modal title="New payment request" onClose={onClose} width={640}>
      <form onSubmit={submit}>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {['existing', 'new'].map(m => (
            <button key={m} type="button" onClick={() => setMode(m)}
              style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid', fontSize: 13, borderColor: mode === m ? '#534AB7' : '#d3d1c7', background: mode === m ? '#534AB7' : '#fff', color: mode === m ? '#fff' : '#444', cursor: 'pointer' }}>
              {m === 'existing' ? 'Select existing beneficiary' : 'Add new beneficiary'}
            </button>
          ))}
        </div>

        {mode === 'existing' ? (
          <Field label="Beneficiary (who the payment is for)" required>
            <select value={selectedBen} onChange={e => setSelectedBen(e.target.value)} required>
              <option value="">— Select beneficiary —</option>
              {beneficiaries.filter(b => b.active).map(b => <option key={b.id} value={b.id}>{b.name} ({b.code})</option>)}
            </select>
            {ben && (
              <div style={{ marginTop: 8, background: '#f5f4f0', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#555' }}>
                <div><strong>Bank:</strong> {ben.bankName || '—'} · <strong>IBAN:</strong> {ben.iban || '—'}</div>
              </div>
            )}
          </Field>
        ) : (
          <div style={{ background: '#f9f8f5', borderRadius: 10, padding: '14px 16px', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: '#444' }}>New beneficiary details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Field label="Full name" required><input value={newBen.name} onChange={e => setNewBen(n => ({...n, name: e.target.value}))} required /></Field>
              <Field label="Type" required><select value={newBen.type} onChange={e => setNewBen(n => ({...n, type: e.target.value}))}>{['individual','company','partner','employee','supplier'].map(t => <option key={t} value={t}>{t}</option>)}</select></Field>
              <Field label="Bank name"><input value={newBen.bankName} onChange={e => setNewBen(n => ({...n, bankName: e.target.value}))} /></Field>
              <Field label="Account number"><input value={newBen.accountNumber} onChange={e => setNewBen(n => ({...n, accountNumber: e.target.value}))} /></Field>
              <Field label="IBAN"><input value={newBen.iban} onChange={e => setNewBen(n => ({...n, iban: e.target.value}))} /></Field>
              <Field label="Swift code"><input value={newBen.swiftCode} onChange={e => setNewBen(n => ({...n, swiftCode: e.target.value}))} /></Field>
            </div>
          </div>
        )}

        <div style={{ height: 1, background: '#eee', margin: '16px 0' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Field label="Amount" required>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="number" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} placeholder="0.00" min="1" required style={{ flex: 1 }} />
              <select value={form.currency} onChange={e => setForm(f => ({...f, currency: e.target.value}))} style={{ width: 80 }}><option>EGP</option><option>USD</option><option>EUR</option></select>
            </div>
          </Field>
          <Field label="Payment type" required>
            <select value={form.paymentTypeId} onChange={e => setForm(f => ({...f, paymentTypeId: e.target.value}))} required>
              <option value="">— Select —</option>
              {paymentTypes.filter(p => p.active).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Job date — from" required><input type="date" value={form.jobDateFrom} onChange={e => setForm(f => ({...f, jobDateFrom: e.target.value}))} required /></Field>
          <Field label="Job date — to" required><input type="date" value={form.jobDateTo} onChange={e => setForm(f => ({...f, jobDateTo: e.target.value}))} min={form.jobDateFrom} required /></Field>
        </div>

        <Field label="Project" required>
          <select value={form.projectId} onChange={e => setForm(f => ({...f, projectId: e.target.value}))} required>
            <option value="">— Select project —</option>
            {myProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>

        <Field label="Payment description" required>
          <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} required rows={3} placeholder="Describe what this payment is for…" />
        </Field>

        {/* ── Reimbursement question ── */}
        <div style={{ background: '#EEEDFE', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
          <Field label="Is the payment made directly to the beneficiary's bank account?" required>
            <select value={form.paymentDirect} onChange={e => setForm(f => ({...f, paymentDirect: e.target.value}))}>
              <option value="yes">Yes — pay the beneficiary directly</option>
              <option value="no">No — beneficiary was paid by cash/Instapay, reimburse someone else</option>
            </select>
          </Field>
          {form.paymentDirect === 'no' && (
            <>
              <Field label="Who receives the bank transfer (reimbursement)?" required>
                <select value={form.recipientType} onChange={e => setForm(f => ({...f, recipientType: e.target.value}))}>
                  <option value="self">Me ({currentUser.name}) — I paid out of pocket</option>
                  <option value="beneficiary">Another registered beneficiary</option>
                </select>
              </Field>
              {form.recipientType === 'beneficiary' && (
                <Field label="Select reimbursement recipient" required>
                  <select value={form.recipientBenId} onChange={e => setForm(f => ({...f, recipientBenId: e.target.value}))} required>
                    <option value="">— Select —</option>
                    {beneficiaries.filter(b => b.active).map(b => <option key={b.id} value={b.id}>{b.name} ({b.code})</option>)}
                  </select>
                </Field>
              )}
              {form.recipientType === 'self' && (
                <div style={{ fontSize: 12, color: '#534AB7', background: '#fff', borderRadius: 8, padding: '8px 12px' }}>
                  Note: Your (the PM's) bank details aren't stored in the system. The accountant will use your details on file for the transfer. The Excel export will mark this as a reimbursement to you.
                </div>
              )}
            </>
          )}
        </div>

        <Field label="Is an invoice available for this payment?" required>
          <select value={form.invoiceAvailable} onChange={e => setForm(f => ({...f, invoiceAvailable: e.target.value, attachedInvoice: e.target.value === 'no' ? '' : f.attachedInvoice}))}>
            <option value="yes">Yes — invoice available</option>
            <option value="no">No — no invoice for this payment</option>
          </select>
        </Field>
        {form.invoiceAvailable === 'yes' && (
          <MultiFileUpload label="Invoices" value={form.attachedInvoice} onChange={v => setForm(f => ({...f, attachedInvoice: v}))} max={10} />
        )}
        <MultiFileUpload label="Supporting documents" value={form.attachedDoc} onChange={v => setForm(f => ({...f, attachedDoc: v}))} max={10} />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={submitting}>{submitting ? 'Submitting…' : 'Submit to accountant'}</Btn>
        </div>
      </form>
    </Modal>
  );
}
